import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { ETranslationNs, IAccountLanguagesForm, ILanguageBaseWithId } from 'src/types'
import { useLanguages } from 'src/hooks'
import { useEffect, useMemo } from 'react'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/langauges-list/constants'

import { useStyles } from './styles'

export const LanguagesInput = () => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Navbar])
  const { values, setFieldValue } = useFormikContext<IAccountLanguagesForm>()
  const { data: languages, isLoading } = useLanguages({ enabled: true })

  const classes = useStyles()

  const value = useMemo(() => {
    return languages?.data?.filter((language) => values.languagesIds.includes(language.id)) || []
  }, [languages, values.languagesIds])

  useEffect(() => {
    if (!languages?.data) return

    const defaultLanguage = languages.data.find(
      (language) => language.code === DEFAULT_LANGUAGE_CODE,
    )

    if (!defaultLanguage) return
    if (values.languagesIds.some((id) => id === defaultLanguage.id)) return

    setFieldValue('languagesIds', [...values.languagesIds, defaultLanguage.id])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (event: any, values: ILanguageBaseWithId[]) => {
    const ids = values.map((value) => value.id)
    setFieldValue('languagesIds', ids)
  }

  return (
    <Autocomplete
      sx={{
        '.MuiAutocomplete-root': {
          position: 'relative',
        },
        '.MuiInputBase-root.MuiInput-root': {
          position: 'relative',
          paddingTop: '30px',
          paddingRight: 0,
        },
        '.MuiAutocomplete-endAdornment': {
          top: 0,
        },
        'MuiPopper-root MuiAutocomplete-popper': {
          width: '500px !important',
        },
        '.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          position: 'absolute',
          top: 0,
          left: 0,
          minWidth: '175px',
        },
      }}
      componentsProps={{
        popper: {
          sx: {
            transform: 'translate3d(0px, 30px, 0px) !important',
          },
        },
      }}
      multiple
      disablePortal
      autoHighlight
      disableClearable
      disableCloseOnSelect
      renderTags={(value, getTagProps) => {
        return (
          <>
            {/* <Typography variant="caption" className={classes.languagesLabel}>
              <HelperWrapper t="languages-header-title" ns={ETranslationNs.Navbar} />
            </Typography> */}
            <div className={classes.languagesList}>
              {value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  disabled={option.code === DEFAULT_LANGUAGE_CODE}
                  key={index}
                  label={option.name}
                />
              ))}
            </div>
          </>
        )
      }}
      getOptionDisabled={(option) => option.code === DEFAULT_LANGUAGE_CODE}
      options={languages?.data || []}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={value}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option, state) => (
        <li {...props}>
          <Checkbox size="small" style={{ marginRight: 8 }} checked={state.selected} />
          {option.name} - {option.code}
        </li>
      )}
      fullWidth
      onChange={handleChange}
      loading={isLoading}
      loadingText={t('loading-text-notification.v')}
      renderInput={(props) => (
        <>
          <TextField
            {...props}
            autoComplete="off"
            variant="standard"
            style={{ height: '100%' }}
            InputProps={{ ...props.InputProps, disableUnderline: true }}
            size="small"
            id={'languagesIds'}
            name={'languagesIds'}
            focused
            placeholder={t('languages-input-placeholder.v')}
            type="text"
          />
        </>
      )}
    />
  )
}
